import { ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  FolderKanban,
  Building2,
  UserCircle,
  HardHat,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import LanguageSwitcher from './LanguageSwitcher'
import NotificationBell from './NotificationBell'
import Breadcrumbs from './Breadcrumbs'
import { UnverifiedEmailBanner } from './UnverifiedEmailBanner'
// === Wave 14 Agent TRIAL BEGIN ===
import { TrialCountdownBanner } from './Trial/TrialCountdownBanner'
import { AnnualUpgradePrompt } from './Trial/AnnualUpgradePrompt'
import { NPSSurveyModal } from './Trial/NPSSurveyModal'
import { MilestoneToast } from './Trial/MilestoneToast'
import { OnboardingTour } from './Trial/OnboardingTour'
import { TrialEndLockScreen } from './Trial/TrialEndLockScreen'
// === Wave 14 Agent TRIAL END ===
// === AGENT_A (Field App) BEGIN ===
import { toast } from 'sonner'
import PendingSyncBadge from './PendingSyncBadge'
import { useSyncReplayer } from '@/hooks/useSyncQueue'
// === AGENT_A (Field App) END ===
import {
  BarChart3, Truck, Receipt, TrendingUp, FileText, Clock, DollarSign,
  Briefcase, Wallet, Inbox, CreditCard, ClipboardList,
  ShieldCheck, FileWarning, ListChecks, LayoutGrid,
  FilePenLine,
  FileUp,
  FolderOpen,
  Send,
  // === AGENT_D (Invoice Scanning) BEGIN ===
  ScanLine,
  // === AGENT_D (Invoice Scanning) END ===
  // === AGENT_E (Tax Suite) BEGIN ===
  Calculator, Percent, Landmark,
  // === AGENT_E (Tax Suite) END ===
  Clipboard,
  // === AGENT_H (Frontend Set 1) BEGIN ===
  Brain, Moon, Coins, Globe, MessageCircle, AlertTriangle,
  // === AGENT_H END ===
  // === AGENT_J (Frontend Set 3) BEGIN ===
  Ruler, IdCard,
  // === AGENT_J (Frontend Set 3) END ===
  // === Phase 2 Agent F1 BEGIN ===
  Sparkles,
  // === Phase 2 Agent F1 END ===
  // === Wave 10 Agent G (Project Health) BEGIN ===
  Activity,
  // === Wave 10 Agent G (Project Health) END ===
  // === Wave 13 Agent BIM BEGIN ===
  Box,
  // === Wave 13 Agent BIM END ===
} from 'lucide-react'
import { BRAND } from '@/config/brand'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const isArabic = i18n.language === 'ar'

  // === AGENT_A (Field App) BEGIN ===
  useSyncReplayer({
    onStale: () => {
      toast.warning(
        isArabic
          ? 'تعديلات بدون اتصال أقدم من 24 ساعة. راجعها قبل الرفع.'
          : 'You have edits older than 24h. Review before they sync.',
        {
          action: {
            label: isArabic ? 'مراجعة' : 'Review',
            onClick: () => navigate('/sync/pending'),
          },
          duration: 15000,
        },
      )
    },
  })
  // === AGENT_A (Field App) END ===

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const menuItems = [
    // === Wave 12 Agent 4 BEGIN ===
    { path: '/master/portfolio', icon: <Briefcase className="w-5 h-5" />, label: isArabic ? 'تنفيذي: المحفظة' : 'Executive: Portfolio' },
    { path: '/master/briefing', icon: <Sparkles className="w-5 h-5" />, label: isArabic ? 'تنفيذي: الموجز اليومي' : 'Executive: Briefing' },
    { path: '/master/admin/cross-tenant', icon: <ShieldCheck className="w-5 h-5" />, label: isArabic ? 'تنفيذي: تحليلات المنصة' : 'Executive: Platform' },
    // === Wave 12 Agent 4 END ===
    { path: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" />, label: t('nav.dashboard') },
    // === Wave 10 Agent G (Project Health) BEGIN ===
    { path: '/health/portfolio', icon: <Activity className="w-5 h-5" />, label: isArabic ? 'صحة المشاريع' : 'Project Health' },
    // === Wave 10 Agent G (Project Health) END ===
    { path: '/portfolio', icon: <Briefcase className="w-5 h-5" />, label: t('nav.portfolio') },
    { path: '/portfolio/overhead', icon: <Wallet className="w-5 h-5" />, label: t('nav.overheadPool') },
    { path: '/reports', icon: <BarChart3 className="w-5 h-5" />, label: t('nav.reports') },
    { path: '/tenders', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'العطاءات' : 'Tenders' },
    { path: '/quality/dashboard', icon: <LayoutGrid className="w-5 h-5" />, label: isArabic ? 'لوحة الجودة' : 'Quality Dashboard' },
    { path: '/quality/inspections', icon: <ShieldCheck className="w-5 h-5" />, label: isArabic ? 'التفتيشات' : 'Inspections' },
    { path: '/quality/ncrs', icon: <FileWarning className="w-5 h-5" />, label: isArabic ? 'عدم المطابقة' : 'NCRs' },
    { path: '/quality/punch-list', icon: <ListChecks className="w-5 h-5" />, label: isArabic ? 'قائمة العيوب' : 'Punch List' },
    { path: '/variation-orders', icon: <FilePenLine className="w-5 h-5" />, label: isArabic ? 'أوامر التغيير' : 'Variation Orders' },
    // === Phase 2 Agent F4 BEGIN ===
    { path: '/variations', icon: <FilePenLine className="w-5 h-5" />, label: isArabic ? 'إدارة التغييرات' : 'Variations (VO Mgmt)' },
    // === Phase 2 Agent F4 END ===
    { path: '/dms', icon: <FolderOpen className="w-5 h-5" />, label: t('dms.dashboard') },
    { path: '/dms/drawings', icon: <FileText className="w-5 h-5" />, label: t('dms.drawings') },
    { path: '/dms/submittals', icon: <ClipboardList className="w-5 h-5" />, label: t('dms.submittals') },
    { path: '/dms/rfis', icon: <Inbox className="w-5 h-5" />, label: t('dms.rfis') },
    { path: '/dms/transmittals', icon: <Send className="w-5 h-5" />, label: t('dms.transmittals') },
    // === AGENT_A (Field App) BEGIN ===
    { path: '/field/reports', icon: <Clipboard className="w-5 h-5" />, label: isArabic ? 'تقارير الميدان' : 'Field Reports' },
    // === AGENT_A (Field App) END ===
    // === Wave 11 Agent FIELD BEGIN ===
    { path: '/field-app', icon: <Clipboard className="w-5 h-5" />, label: isArabic ? 'تطبيق الميدان (PWA)' : 'Field App (PWA)' },
    // === Wave 11 Agent FIELD END ===
    { path: '/projects', icon: <FolderKanban className="w-5 h-5" />, label: t('nav.projects') },
    { path: '/companies', icon: <Building2 className="w-5 h-5" />, label: t('nav.companies') },
    { path: '/clients', icon: <UserCircle className="w-5 h-5" />, label: t('nav.clients') },
    { path: '/consultants', icon: <HardHat className="w-5 h-5" />, label: t('nav.consultants') },
    { path: '/suppliers', icon: <Truck className="w-5 h-5" />, label: t('nav.suppliers') },
    // === AGENT_B (Subcontractor) BEGIN ===
    { path: '/subcontractors', icon: <HardHat className="w-5 h-5" />, label: isArabic ? 'المقاولون الفرعيون' : 'Subcontractors' },
    { path: '/subcontracts', icon: <FilePenLine className="w-5 h-5" />, label: isArabic ? 'عقود الباطن' : 'Subcontracts' },
    { path: '/subcontract-payments', icon: <Receipt className="w-5 h-5" />, label: isArabic ? 'شهادات الدفع' : 'Payment Certs' },
    // === AGENT_B (Subcontractor) END ===
    // === Phase 2 Wave 6 Agent M2 (Subcontractor Management) BEGIN ===
    { path: '/subcontractors-v2', icon: <HardHat className="w-5 h-5" />, label: isArabic ? 'إدارة المقاولين (v2)' : 'Subcontractor Mgmt (v2)' },
    // === Phase 2 Wave 6 Agent M2 (Subcontractor Management) END ===
    { path: '/supplier-invoices', icon: <Receipt className="w-5 h-5" />, label: isArabic ? 'فواتير الموردين' : 'Supplier Invoices' },
    { path: '/cashflow', icon: <TrendingUp className="w-5 h-5" />, label: isArabic ? 'التدفق النقدي' : 'Cash Flow' },
    { path: '/ipcs', icon: <DollarSign className="w-5 h-5" />, label: isArabic ? 'شهادات الدفعات' : 'IPCs' },
    // === Wave 10 Agent F BEGIN ===
    { path: '/ipc-fidic', icon: <Receipt className="w-5 h-5" />, label: isArabic ? 'شهادات الدفع - فيديك' : 'IPC (FIDIC)' },
    // === Wave 10 Agent F END ===
    { path: '/purchase-orders', icon: <FileText className="w-5 h-5" />, label: isArabic ? 'أوامر الشراء' : 'Purchase Orders' },
    { path: '/aging', icon: <Clock className="w-5 h-5" />, label: isArabic ? 'تقرير المتأخرات' : 'Aging Reports' },
    { path: '/approvals', icon: <Inbox className="w-5 h-5" />, label: isArabic ? 'الموافقات' : 'Approvals' },
    { path: '/billing/subscription', icon: <CreditCard className="w-5 h-5" />, label: isArabic ? 'الفوترة' : 'Billing' },
    // === AGENT_E (Tax Suite) BEGIN ===
    { path: '/tax/vat/returns', icon: <Calculator className="w-5 h-5" />, label: isArabic ? 'ضريبة القيمة المضافة' : 'VAT Returns' },
    { path: '/tax/ct/returns', icon: <Landmark className="w-5 h-5" />, label: isArabic ? 'ضريبة الشركات' : 'Corporate Tax' },
    { path: '/tax/config', icon: <Percent className="w-5 h-5" />, label: isArabic ? 'إعدادات الضرائب' : 'Tax Settings' },
    // === AGENT_E (Tax Suite) END ===
    { path: '/team', icon: <Users className="w-5 h-5" />, label: t('nav.team') },
    // === AGENT_A (M2.8 Workforce + Payroll) BEGIN ===
    { path: '/workforce/employees', icon: <Users className="w-5 h-5" />, label: isArabic ? 'الموظفون' : 'Employees' },
    { path: '/workforce/departments', icon: <Building2 className="w-5 h-5" />, label: isArabic ? 'الإدارات' : 'Departments' },
    { path: '/workforce/leave', icon: <Inbox className="w-5 h-5" />, label: isArabic ? 'طلبات الإجازة' : 'Leave Requests' },
    { path: '/workforce/attendance', icon: <Clock className="w-5 h-5" />, label: isArabic ? 'الحضور' : 'Attendance' },
    { path: '/payroll/periods', icon: <DollarSign className="w-5 h-5" />, label: isArabic ? 'الرواتب' : 'Payroll' },
    { path: '/payroll/gratuity', icon: <Calculator className="w-5 h-5" />, label: isArabic ? 'حاسبة نهاية الخدمة' : 'Gratuity' },
    // === AGENT_A (M2.8 Workforce + Payroll) END ===
    // === Wave 11 Agent HR BEGIN ===
    { path: '/workforce/dashboard', icon: <Sparkles className="w-5 h-5" />, label: isArabic ? 'القوى العاملة (Enterprise+AI)' : 'Workforce (Enterprise+AI)' },
    { path: '/workforce/anti-ghost', icon: <ShieldCheck className="w-5 h-5" />, label: isArabic ? 'مكافحة العمال الوهميين' : 'Anti-ghost-worker' },
    { path: '/workforce/project-cost', icon: <BarChart3 className="w-5 h-5" />, label: isArabic ? 'تكلفة العمالة للمشروع' : 'Project labour cost' },
    { path: '/workforce/productivity', icon: <Brain className="w-5 h-5" />, label: isArabic ? 'إنتاجية الذكاء الاصطناعي' : 'AI productivity' },
    // === Wave 11 Agent HR END ===
    // === Phase 2 Wave 5 Agent M1 BEGIN ===
    { path: '/hse/wave5/dashboard', icon: <ShieldCheck className="w-5 h-5" />, label: isArabic ? 'الامتثال (HSE)' : 'HSE Compliance' },
    { path: '/hse/wave5/permits', icon: <HardHat className="w-5 h-5" />, label: isArabic ? 'تصاريح العمل' : 'PTW Catalog' },
    { path: '/hse/wave5/incidents', icon: <AlertTriangle className="w-5 h-5" />, label: isArabic ? 'بلاغات شبه الحوادث' : 'Near-misses' },
    { path: '/hse/wave5/toolbox-talks', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'محاضرات السلامة' : 'Toolbox Talks' },
    // === Phase 2 Wave 5 Agent M1 END ===
    { path: '/settings', icon: <Settings className="w-5 h-5" />, label: t('nav.settings') },
    // === AGENT_D (Invoice Scanning) BEGIN ===
    { path: '/invoice-scans', icon: <ScanLine className="w-5 h-5" />, label: isArabic ? 'مسح الفواتير' : 'Invoice Scans' },
    { path: '/invoice-scans/stats', icon: <BarChart3 className="w-5 h-5" />, label: isArabic ? 'إنفاق Claude' : 'Claude Spend' },
    // === AGENT_D (Invoice Scanning) END ===
    // === AGENT_H (Frontend Set 1) BEGIN ===
    { path: '/predictive', icon: <Brain className="w-5 h-5" />, label: isArabic ? 'التحليلات التنبؤية' : 'Predictive AI' },
    { path: '/predictive/alerts', icon: <AlertTriangle className="w-5 h-5" />, label: isArabic ? 'إنذارات التنبؤ' : 'Predictive Alerts' },
    { path: '/chat', icon: <MessageCircle className="w-5 h-5" />, label: isArabic ? 'المساعد الذكي' : 'AI Assistant' },
    { path: '/hijri', icon: <Moon className="w-5 h-5" />, label: isArabic ? 'التقويم الهجري' : 'Hijri Calendar' },
    { path: '/currency-conversions', icon: <Coins className="w-5 h-5" />, label: isArabic ? 'تحويلات العملات' : 'FX Conversions' },
    { path: '/locale-settings', icon: <Globe className="w-5 h-5" />, label: isArabic ? 'اللغة والعملة' : 'Locale Settings' },
    // === AGENT_H END ===
    // === AGENT_I (Frontend Set 2) BEGIN ===
    { path: '/dms/letters', icon: <Send className="w-5 h-5" />, label: isArabic ? 'المراسلات' : 'Letters' },
    { path: '/security/dashboard', icon: <ShieldCheck className="w-5 h-5" />, label: isArabic ? 'مركز الأمان' : 'Security' },
    { path: '/materials/catalog', icon: <Truck className="w-5 h-5" />, label: isArabic ? 'فهرس المواد' : 'Materials' },
    // === Wave 11 Agent MAT BEGIN ===
    { path: '/materials/lifecycle/requests', icon: <Truck className="w-5 h-5" />, label: isArabic ? 'طلبات المواد W11' : 'Requests (W11)' },
    { path: '/materials/lifecycle/consumption', icon: <Truck className="w-5 h-5" />, label: isArabic ? 'سجل الاستهلاك W11' : 'Consumption (W11)' },
    { path: '/materials/lifecycle/theft-alerts', icon: <Truck className="w-5 h-5" />, label: isArabic ? 'تنبيهات السرقة W11' : 'Theft Alerts (W11)' },
    { path: '/materials/lifecycle/reconciliation', icon: <Truck className="w-5 h-5" />, label: isArabic ? 'تسوية المواد W11' : 'Reconciliation (W11)' },
    // === Wave 11 Agent MAT END ===
    { path: '/sustainability', icon: <LayoutGrid className="w-5 h-5" />, label: isArabic ? 'الاستدامة' : 'Sustainability' },
    // === AGENT_I (Frontend Set 2) END ===
    // === AGENT_J (Frontend Set 3) BEGIN ===
    { path: '/cost-estimation', icon: <Calculator className="w-5 h-5" />, label: isArabic ? 'تقدير التكاليف' : 'Cost Estimation' },
    // === Phase 2 Agent F2 BEGIN ===
    { path: '/estimation/ai', icon: <Brain className="w-5 h-5" />, label: isArabic ? 'تقدير ذكي بالـAI' : 'AI Cost Estimation' },
    // === Phase 2 Agent F2 END ===
    // === Phase 2 Wave 5 Agent E3 BEGIN ===
    { path: '/analytics/estimation', icon: <BarChart3 className="w-5 h-5" />, label: isArabic ? 'تحليلات التقدير' : 'Estimation Analytics' },
    // === Phase 2 Wave 5 Agent E3 END ===
    { path: '/qto/drawings', icon: <Ruler className="w-5 h-5" />, label: isArabic ? 'حصر الكميات' : 'Quantity Takeoff' },
    // === Phase 2 Agent F1 BEGIN ===
    { path: '/qto/auto-mapping/0', icon: <Sparkles className="w-5 h-5" />, label: isArabic ? 'الحصر التلقائي AI' : 'AI Auto-Mapping' },
    // === Phase 2 Agent F1 END ===
    // === Wave 13 Agent BIM BEGIN ===
    { path: '/bim/import', icon: <Box className="w-5 h-5" />, label: isArabic ? 'استيراد BIM' : 'BIM Import' },
    // === Wave 13 Agent BIM END ===
    { path: '/govt', icon: <Landmark className="w-5 h-5" />, label: isArabic ? 'تكاملات حكومية' : 'Govt Integrations' },
    { path: '/govt/eida', icon: <IdCard className="w-5 h-5" />, label: isArabic ? 'تحقق الهوية' : 'Emirates ID' },
    // === AGENT_J (Frontend Set 3) END ===
    // === AGENT_FE-3 (Run #10 — QA/QC + Set 3 polish) BEGIN ===
    { path: '/qa-qc/inspections', icon: <ShieldCheck className="w-5 h-5" />, label: isArabic ? 'تفتيش الجودة' : 'QA/QC' },
    { path: '/govt/compliance', icon: <Landmark className="w-5 h-5" />, label: isArabic ? 'لوحة الامتثال' : 'Compliance' },
    // === AGENT_FE-3 (Run #10) END ===
    // === AGENT_S4_G (QA/QC Phase 2 FE) BEGIN ===
    { path: '/qaqc', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'لوحة QA/QC' : 'QA/QC Hub' },
    // === AGENT_S4_G (QA/QC Phase 2 FE) END ===
    // === Wave 11 Agent QC2 BEGIN ===
    { path: '/qaqc/extensions/material-tests', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'اختبارات المواد' : 'Material Tests' },
    { path: '/qaqc/extensions/cube-tests', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'اختبارات المكعبات' : 'Cube Tests' },
    { path: '/qaqc/extensions/slump-tests', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'اختبارات الهبوط' : 'Slump Tests' },
    { path: '/qaqc/extensions/concrete-pours', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'سجل الصب' : 'Concrete Pours' },
    { path: '/qaqc/extensions/mix-designs', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'مكتبة الخلطات' : 'Mix Designs' },
    // === Wave 11 Agent QC2 END ===
    // === AGENT_FE-1 (Run #10 — Client Portal) BEGIN ===
    { path: '/portal/login', icon: <UserCircle className="w-5 h-5" />, label: isArabic ? 'بوابة العملاء' : 'Client Portal' },
    // === AGENT_FE-1 (Run #10 — Client Portal) END ===
    // === Phase 2 Agent D1 BEGIN ===
    { path: '/boq/premium-import', icon: <FileUp className="w-5 h-5" />, label: isArabic ? 'مستورد الكميات المميز' : 'BOQ Premium Importer' },
    // === Phase 2 Agent D1 END ===
    // === Phase 2 Wave 7 Agent M7 BEGIN ===
    { path: '/dms-premium', icon: <FolderOpen className="w-5 h-5" />, label: isArabic ? 'إدارة الوثائق المميزة' : 'DMS Premium' },
    { path: '/dms-premium/drawings', icon: <FileText className="w-5 h-5" />, label: isArabic ? 'المخططات (مميز)' : 'Drawings (Premium)' },
    { path: '/dms-premium/submittals', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'العينات والموافقات' : 'Submittals' },
    { path: '/dms-premium/rfis', icon: <Inbox className="w-5 h-5" />, label: isArabic ? 'طلبات المعلومات' : 'RFIs' },
    { path: '/dms-premium/transmittals', icon: <Send className="w-5 h-5" />, label: isArabic ? 'كتب التحويل' : 'Transmittals' },
    // === Phase 2 Wave 7 Agent M7 END ===
    // === Wave 14 Agent POLISH BEGIN ===
    { path: '/procurement', icon: <Truck className="w-5 h-5" />, label: isArabic ? 'مركز المشتريات' : 'Procurement Hub' },
    { path: '/procurement/rfqs', icon: <FileText className="w-5 h-5" />, label: isArabic ? 'طلبات عروض الأسعار' : 'RFQs' },
    { path: '/procurement/pos', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'أوامر الشراء' : 'POs (Hub)' },
    { path: '/procurement/grns', icon: <Receipt className="w-5 h-5" />, label: isArabic ? 'إيصالات الاستلام' : 'GRNs' },
    { path: '/procurement/analytics', icon: <BarChart3 className="w-5 h-5" />, label: isArabic ? 'تحليلات المشتريات' : 'Procurement Analytics' },
    { path: '/admin/rbac-premium', icon: <ShieldCheck className="w-5 h-5" />, label: isArabic ? 'صلاحيات متقدمة' : 'RBAC Premium' },
    { path: '/admin/role-builder', icon: <Users className="w-5 h-5" />, label: isArabic ? 'بناء الأدوار' : 'Role Builder' },
    { path: '/admin/access-anomalies', icon: <AlertTriangle className="w-5 h-5" />, label: isArabic ? 'شذوذ الوصول' : 'Access Anomalies' },
    { path: '/admin/approval-workflows', icon: <Inbox className="w-5 h-5" />, label: isArabic ? 'سير الموافقات' : 'Approval Workflows' },
    { path: '/analytics/benchmarks', icon: <BarChart3 className="w-5 h-5" />, label: isArabic ? 'مقاييس الصناعة' : 'Industry Benchmarks' },
    { path: '/cost-comparison', icon: <TrendingUp className="w-5 h-5" />, label: isArabic ? 'مقارنة التكاليف' : 'Cost Comparison' },
    { path: '/cost-alerts', icon: <AlertTriangle className="w-5 h-5" />, label: isArabic ? 'تنبيهات التكلفة' : 'Cost Alerts' },
    { path: '/tax/dashboard', icon: <Calculator className="w-5 h-5" />, label: isArabic ? 'مركز الضرائب' : 'Tax Hub' },
    { path: '/tenders/bidding', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'إدارة العطاءات' : 'Bidding Pipeline' },
    { path: '/qto/4d5d', icon: <Ruler className="w-5 h-5" />, label: isArabic ? 'حصر 4D/5D' : 'QTO 4D/5D' },
    { path: '/qto/review', icon: <ClipboardList className="w-5 h-5" />, label: isArabic ? 'مراجعة الحصر' : 'QTO Review' },
    { path: '/materials/warehouses', icon: <Truck className="w-5 h-5" />, label: isArabic ? 'المستودعات' : 'Warehouses' },
    { path: '/materials/takeoff', icon: <Ruler className="w-5 h-5" />, label: isArabic ? 'حصر المواد' : 'Material Takeoff' },
    { path: '/billing/dashboard', icon: <CreditCard className="w-5 h-5" />, label: isArabic ? 'لوحة الفواتير' : 'Billing Dashboard' },
    // === Wave 14 Agent POLISH END ===
  ]

  const ChevronIcon = isArabic ? ChevronLeft : ChevronRight
  const isProfileActive = location.pathname === '/profile'

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-72 bg-gradient-to-b from-gray-900 to-gray-800 flex flex-col shadow-2xl">
        {/* Logo Section */}
        <div className="px-6 py-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br ${BRAND.gradient}`}>
              <span className="text-white text-2xl font-bold">{isArabic ? BRAND.initialAr : BRAND.initial}</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white leading-tight">
                {user?.tenant ? (isArabic ? (user.tenant.company_name_ar || user.tenant.company_name_en) : user.tenant.company_name_en) : (isArabic ? BRAND.nameAr : BRAND.name)}
              </h1>
              <p className="text-xs text-gray-400">
                {isArabic ? BRAND.taglineAr : BRAND.tagline}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.path === '/projects' && location.pathname.startsWith('/projects')) ||
              (item.path === '/clients' && location.pathname.startsWith('/clients')) ||
              (item.path === '/consultants' && location.pathname.startsWith('/consultants')) ||
              (item.path === '/billing/subscription' && location.pathname.startsWith('/billing')) ||
              (item.path === '/field/reports' && location.pathname.startsWith('/field/reports')) ||
              (item.path === '/predictive' && location.pathname === '/predictive') ||
              (item.path === '/chat' && location.pathname.startsWith('/chat/sessions')) ||
              (item.path === '/dms/letters' && location.pathname.startsWith('/dms/letters')) ||
              (item.path === '/security/dashboard' && location.pathname.startsWith('/security')) ||
              (item.path === '/materials/catalog' && location.pathname.startsWith('/materials')) ||
              (item.path === '/sustainability' && location.pathname.startsWith('/sustainability')) ||
              (item.path === '/cost-estimation' && location.pathname.startsWith('/cost-estimation')) ||
              // === Phase 2 Agent F1 BEGIN ===
              (item.path === '/qto/auto-mapping/0' && location.pathname.startsWith('/qto/auto-mapping')) ||
              // === Phase 2 Agent F1 END ===
              (item.path === '/qto/drawings' && location.pathname.startsWith('/qto') && !location.pathname.startsWith('/qto/auto-mapping')) ||
              (item.path === '/govt' && location.pathname === '/govt') ||
              (item.path === '/qa-qc/inspections' && location.pathname.startsWith('/qa-qc')) ||
              (item.path === '/govt/compliance' && location.pathname === '/govt/compliance') ||
              (item.path === '/qaqc' && location.pathname.startsWith('/qaqc')) ||
              // === Phase 2 Wave 7 Agent M7 BEGIN ===
              (item.path === '/dms-premium' && location.pathname === '/dms-premium') ||
              (item.path === '/dms-premium/drawings' && location.pathname.startsWith('/dms-premium/drawings')) ||
              (item.path === '/dms-premium/submittals' && location.pathname.startsWith('/dms-premium/submittals')) ||
              (item.path === '/dms-premium/rfis' && location.pathname.startsWith('/dms-premium/rfis')) ||
              (item.path === '/dms-premium/transmittals' && location.pathname.startsWith('/dms-premium/transmittals')) ||
              // === Phase 2 Wave 7 Agent M7 END ===
              // === Wave 14 Agent POLISH BEGIN ===
              (item.path === '/procurement' && location.pathname === '/procurement') ||
              (item.path === '/procurement/rfqs' && location.pathname.startsWith('/procurement/rfqs')) ||
              (item.path === '/procurement/pos' && location.pathname.startsWith('/procurement/pos')) ||
              (item.path === '/procurement/grns' && location.pathname.startsWith('/procurement/grns')) ||
              (item.path === '/tax/dashboard' && location.pathname === '/tax/dashboard') ||
              (item.path === '/tenders/bidding' && location.pathname.startsWith('/tenders/bidding')) ||
              (item.path === '/admin/rbac-premium' && location.pathname === '/admin/rbac-premium')
              // === Wave 14 Agent POLISH END ===
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </div>
                <ChevronIcon className="w-4 h-4 opacity-50" />
              </button>
            )
          })}
        </nav>

        {/* Pending sync badge */}
        <div className="px-4 pt-3 flex justify-center">
          <PendingSyncBadge />
        </div>

        {/* Language Switcher + Notifications */}
        <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between gap-2">
          <LanguageSwitcher />
          <NotificationBell />
        </div>

        {/* User Section */}
        <div className="px-4 py-4 border-t border-gray-700 bg-gray-900/50">
          <button
            onClick={() => navigate('/profile')}
            className={`w-full flex items-center gap-3 mb-3 p-2 rounded-lg transition-all ${
              isProfileActive
                ? 'bg-blue-600/20 ring-2 ring-blue-600/50'
                : 'hover:bg-gray-700/50'
            }`}
            title={t('profile.viewProfile')}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
              {user?.full_name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0 text-start">
              <p className="text-sm font-medium text-white truncate">{user?.full_name || 'User'}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </button>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors text-sm"
          >
            <LogOut className="w-4 h-4" />
            {t('nav.logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* === Wave 14 Agent TRIAL BEGIN === */}
        <TrialCountdownBanner />
        {/* === Wave 14 Agent TRIAL END === */}
        <UnverifiedEmailBanner />
        <Breadcrumbs />
        {children}
        {/* === Wave 14 Agent TRIAL BEGIN === */}
        <AnnualUpgradePrompt />
        <NPSSurveyModal />
        <MilestoneToast />
        <OnboardingTour />
        <TrialEndLockScreen />
        {/* === Wave 14 Agent TRIAL END === */}
      </main>
    </div>
  )
}